import { ThemeProvider } from '@mui/material';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PersistGate } from 'redux-persist/integration/react';
import './App.css';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { toastify } from './components/Common/ToastMessage/Toastify';
import { BaseLayout } from './components/Layout/BaseLayout';
import Home from './pages/Home/Home';
import PageNotFound from './pages/PageNotFound/PageNotFound';
import SettingsPage from './pages/Settings/SettingsPage';
import WebhooksPage from './pages/Webhooks/WebhooksPage';
import { logout } from './redux/slices/authSlice';
import store, { persistor } from './redux/store';
import theme from './theme';

function App() {
  const checkExpiredAndLogout = async (error: any) => {
    // Check Expired Time here to logout;
    try {
      // Check user session
    } catch (err: any) {
      if (store.getState()?.auth?.user) {
        queryClient.invalidateQueries();
        persistor.purge();
        store.dispatch(logout());
        toastify.error('Your session has expired. Please Sign in again');
      }
    }
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
        onError: (error: any) => {
          // Fallback Error Catch If we don't define onError when using useMutate
          toastify.error(error?.message ?? 'Something went wrong.');
        },
      },
    },
    queryCache: new QueryCache({
      onError: checkExpiredAndLogout,
    }),
    mutationCache: new MutationCache({
      onError: checkExpiredAndLogout,
    }),
  });
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools />
      <HelmetProvider>
        <Helmet>
          <title>Clockify Integration</title>
        </Helmet>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <ThemeProvider theme={theme}>
              <BrowserRouter>
                <Routes>
                  <Route element={<BaseLayout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/manage-webhooks" element={<WebhooksPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route
                      element={
                        <ProtectedRoute>
                          <Outlet />
                        </ProtectedRoute>
                      }
                    >
                      {/* Private routes here */}
                    </Route>
                  </Route>
                  <Route path="/*" element={<PageNotFound />} />
                </Routes>
              </BrowserRouter>
              <ToastContainer
                className="toaster-container"
                position="bottom-right"
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                closeButton={false}
              />
            </ThemeProvider>
          </PersistGate>
        </Provider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
